import React, { useEffect, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import {
  CheckPayment,
  ICheckPay,
  PaymentStatusLoading,
  RESULT_PAYMENT,
  SERVICE_TYPE,
} from 'shared/api/check-payment'
import { replaceWindowHistory } from 'shared/lib/replaceWindowHistory'
import { trackAdjustEvent } from 'shared/services/adjust'

import { AviaSuccess } from './components/avia-success'
import { BeketPayment } from './components/beket-success'
import { EsimSuccess } from './components/esim-success'
import { ExcursionSuccess } from './components/excursion-success'
import { InsuranceSuccess } from './components/insurance-success'
import { KyrgyzstanPayment } from './components/kyrgyzstan-success'
import { ToursPayment } from './components/tours-success'
import { UmrahPayment } from './components/umrah-success'
import { getPreviewPayment } from './lib/get-preview-payment'
import styles from './styles.module.scss'

const paymentPages = {
  [SERVICE_TYPE.TICKETS]: AviaSuccess,
  [SERVICE_TYPE.EXCURSIONS]: ExcursionSuccess,
  [SERVICE_TYPE.FLIGHTS]: AviaSuccess,
  [SERVICE_TYPE.BEKET]: BeketPayment,
  [SERVICE_TYPE.TOURS]: ToursPayment,
  [SERVICE_TYPE.UMRA]: UmrahPayment,
  [SERVICE_TYPE.KYRGYZSTAN]: KyrgyzstanPayment,
  [SERVICE_TYPE.INSURANCE]: InsuranceSuccess,
  [SERVICE_TYPE.ESIM]: EsimSuccess,
}

const eachTimeRequest = 1500

const requestStop = 120000

const PaymentSuccess = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const paymentId = searchParams.get('payment_id')
  const previewPayment = getPreviewPayment(searchParams, import.meta.env.DEV)

  const [payment, setPayment] = useState<ICheckPay>(
    previewPayment ?? {
      status: RESULT_PAYMENT.LOADING,
      service_type: SERVICE_TYPE.FLIGHTS,
    },
  )

  const timerId = useRef<ReturnType<typeof setInterval> | null>(null)
  const finishTimerId = useRef<ReturnType<typeof setTimeout> | null>(null)

  const checkPayment = async (paymentId: string) => {
    try {
      const { service_type, status } = await getCurrentStatusRequest(paymentId)

      if (!Object.prototype.hasOwnProperty.call(paymentPages, service_type)) {
        stopAllTimers()
        navigate(RoutingPaths.path(), { replace: true })
        return
      }

      if (status === RESULT_PAYMENT.SUCCESS) {
        if (service_type === SERVICE_TYPE.TICKETS) {
          void trackAdjustEvent('aviaPurchase', paymentId)
        }

        setPayment({ service_type, status })

        stopAllTimers()
      }

      if (payment.status !== status) {
        setPayment((state) => {
          if (state.status !== RESULT_PAYMENT.CANCELLED) {
            if (state.status !== status) {
              return {
                status: status,
                service_type: service_type,
              }
            }

            return state
          }

          return {
            service_type: service_type,
            status: RESULT_PAYMENT.CANCELLED,
          }
        })
      }

      if (PaymentStatusLoading.includes(status) && !timerId.current) {
        startLoader(paymentId, service_type)
      }
    } catch {
      stopAllTimers()

      setPayment({ ...payment, status: RESULT_PAYMENT.CANCELLED })
    }
  }

  const startLoader = (paymentId: string, service_type: SERVICE_TYPE) => {
    timerId.current = setInterval(() => {
      checkPayment(paymentId)
    }, eachTimeRequest)

    finishTimerId.current = setTimeout(() => {
      loaderStop(service_type)
    }, requestStop)
  }

  const loaderStop = (service: SERVICE_TYPE) => {
    stopAllTimers()

    setPayment({
      service_type: service,
      status: RESULT_PAYMENT.CANCELLED,
    })
  }

  const renderSuccessPage = () => {
    const PaymentPage = paymentPages[payment.service_type]

    if (!PaymentPage) {
      return null
    }

    return React.createElement(PaymentPage, {
      status: payment.status,
    })
  }

  const getCurrentStatusRequest = async (paymentId: string) => {
    const { data: CheckPaymentData } =
      await CheckPayment.checkPaymentRequest(paymentId)

    return {
      ...CheckPaymentData?.data,
    }
  }

  const stopAllTimers = () => {
    if (timerId.current !== null) {
      clearInterval(timerId.current)
      timerId.current = null
    }
    if (finishTimerId.current !== null) {
      clearTimeout(finishTimerId.current)
      finishTimerId.current = null
    }
  }

  useEffect(() => {
    if (previewPayment) return

    if (!paymentId) {
      return navigate(RoutingPaths.path())
    }

    checkPayment(paymentId)

    replaceWindowHistory(null, '', RoutingPaths.path())
    return () => {
      stopAllTimers()
    }
  }, [])

  return <div className={styles.container}>{renderSuccessPage()}</div>
}

export default PaymentSuccess
